import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleContent } from '@/mocks/article-content';
import { useJsonLd } from '@/hooks/useJsonLd';
import type { ArticleSection } from '@/mocks/article-content';
import { SITE_URL } from '@/constants/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  const article = slug ? getArticleContent(slug) : null;

  const formatDateISO = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toISOString().split('T')[0];
  };

  const articleJsonLd = article ? {
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.title,
    url: SITE_URL + '/article/' + article.slug,
    datePublished: formatDateISO(article.date),
    dateModified: formatDateISO(article.date),
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'LitBuy Spreadsheets',
      url: SITE_URL + '/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': SITE_URL + '/article/' + article.slug,
    },
  } : null;

  useJsonLd(articleJsonLd);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = article.title + ' | LitBuy Spreadsheets';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDesc = metaDesc.getAttribute('content') || '';
    metaDesc.setAttribute('content', article.sections.find((s: ArticleSection) => s.type === 'paragraph')?.content?.slice(0, 155) || article.title);
    return () => {
      document.title = prevTitle;
      metaDesc?.setAttribute('content', prevDesc);
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-3xl mx-auto text-center py-20">
            <h1 className="font-heading text-3xl font-bold text-foreground-900">Article Not Found</h1>
            <p className="mt-4 text-foreground-600">The article you are looking for does not exist yet.</p>
            <Link
              to="/"
              className="inline-block mt-6 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article>
          <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/50" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block px-3 py-1 rounded-full bg-background-50/80 backdrop-blur-sm text-xs text-foreground-800 font-medium mb-3">
                  {article.category}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight max-w-3xl">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>

          <nav className="max-w-3xl mx-auto px-6 md:px-10 pt-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700">{article.category}</li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-400 truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-12">
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400" />
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400" />
              <span>{article.readTime}</span>
            </div>

            <div className="prose-custom">
              {article.sections.map((section: ArticleSection, index: number) => {
                if (section.type === 'heading') {
                  return (
                    <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {section.content}
                    </h2>
                  );
                }
                if (section.type === 'subheading') {
                  return (
                    <h3 key={index} className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-8 mb-3">
                      {section.content}
                    </h3>
                  );
                }
                if (section.type === 'paragraph') {
                  return (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  );
                }
                if (section.type === 'table') {
                  return (
                    <div key={index} className="overflow-x-auto my-8 rounded-xl border border-background-200/70">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            {section.headers?.map((header: string, hIdx: number) => (
                              <th
                                key={hIdx}
                                className="px-4 py-3 text-left font-heading font-semibold text-foreground-900 whitespace-nowrap"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.rows?.map((row: string[], rIdx: number) => (
                            <tr key={rIdx} className="border-t border-background-200/60">
                              {row.map((cell: string, cIdx: number) => (
                                <td
                                  key={cIdx}
                                  className={`px-4 py-3 whitespace-nowrap ${
                                    cIdx === 0 ? 'font-medium text-foreground-900' : 'text-foreground-600'
                                  }`}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                    LitBuy Spreadsheet
                  </span>
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {article.readTime}
                  </span>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </span>
                  Back to Home
                </Link>
              </div>
            </div>

            {article.relatedArticles && article.relatedArticles.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                  Related Articles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {article.relatedArticles.map((related: { slug: string; title: string }) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="block p-4 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                    >
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <span className="inline-flex items-center gap-1 text-xs text-primary-600 mt-2">
                        Read More
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}